import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import ClaimAdditionalSupportNotice from './ClaimAdditionalSupportNotice'
import ClaimGstItcFields, { type ClaimGstItcFieldsHandle } from './ClaimGstItcFields'
import { ChevronRightIcon, FormCard, PrimaryButton } from './ui'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import { clientLogInfo, clientLogWarn } from '../utils/clientLog'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import {
  CLAIM_FLOW_SUMMARY_WINDSCREEN,
  formatClaimPolicySummaryLine,
  formatIncidentRailSummary,
  formatIncidentReviewDateTime,
  formatIncidentReviewLocation,
} from '../claims/claimFlowModel'
import { buildFnolSubmitAgentEmailRows } from '../claims/agentEmailDetailRows'
import type { ClaimFlowDraft } from '../claims/claimDraftStorage'
import { clearClaimDraft, readClaimDraft } from '../claims/claimDraftStorage'
import ClaimAdditionalExcessesModal from './ClaimAdditionalExcessesModal'
import {
  ClaimReviewCollapsibleDescription,
  ClaimReviewSummaryRow,
} from './ClaimReviewSummaryRow'
import { formatSumInsuredSnippet } from '../claims/windscreenExcessUtils'
import { useApi } from '../services/api'
import { useScrollToTopOnErrorSignal } from '../hooks/useScrollToTopOnErrorSignal'
import { isDdMmYyyyInLocalRange, PORTAL_DATE_MIN, startOfTodayLocal } from '../utils/dobDateInput'
import { isIncidentDdMmYyyyBeforePolicyStart } from '../utils/policyCoverStart'
import {
  TRUSTED_WINDSCREEN_REPAIRER_COMPANY,
  trustedWindscreenRepairerCompanyLabel,
} from '../claims/windscreenRepairerConstants'

/** Matches {@link ClaimIncidentStep} structured-address rule for AU postcodes. */
function isValidAuPostcodeDigits(s: string): boolean {
  return /^\d{4}$/.test(s.trim())
}

function buildWindscreenSearch(policyRef: string | null, typeInQuery: string | null): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (typeInQuery) p.set('type', typeInQuery)
  const q = p.toString()
  return q ? `?${q}` : ''
}

function trimToNull(value: string | null | undefined): string | null {
  if (value == null) return null
  const t = value.trim()
  return t.length > 0 ? t : null
}

function repairChoiceLabel(option: ClaimFlowDraft['windscreenRepairOption'], draft: ClaimFlowDraft | null): string {
  if (draft?.windscreenReplacedAlready === true) {
    return 'Not applicable — windscreen already repaired or replaced'
  }
  if (option === 'trusted') return 'Club 4X4 trusted repairer'
  if (option === 'own') return 'Own repairer'
  return 'NA'
}

function repairerDetailsSummary(draft: ClaimFlowDraft | null): string {
  if (!draft) return 'None provided'
  if (draft.windscreenReplacedAlready === true) {
    const parts = [
      draft.windscreenRepairerCompany?.trim(),
      draft.windscreenRepairerEmail?.trim(),
      draft.windscreenRepairerPhone?.trim(),
    ].filter(Boolean)
    if (parts.length === 0) return 'None provided'
    return parts.join(' · ')
  }
  if (draft.windscreenRepairOption === 'trusted') {
    return trustedWindscreenRepairerCompanyLabel(draft.windscreenRepairerCompany)
  }
  if (draft.windscreenRepairOption !== 'own') return 'None provided'
  const parts = [
    draft.windscreenRepairerCompany?.trim(),
    draft.windscreenRepairerEmail?.trim(),
    draft.windscreenRepairerPhone?.trim(),
  ].filter(Boolean)
  if (parts.length === 0) return 'None provided'
  return parts.join(' · ')
}

/**
 * Windscreen branch: confirm details, excesses, and submit FNOL.
 */
export default function ClaimWindscreenReviewStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const { submitWindscreenClaim } = useApi()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const navState = location.state as {
    policy?: PolicyLink
    preserveScroll?: boolean
    windscreenClaimSubmitted?: boolean
    claimNumber?: string | null
  } | null

  const policy = navState?.policy
  const draft = readClaimDraft()

  const gstFieldsRef = useRef<ClaimGstItcFieldsHandle>(null)
  const [gstItcComplete, setGstItcComplete] = useState(false)
  const [gstSubmitAttempted, setGstSubmitAttempted] = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [additionalExcessModalOpen, setAdditionalExcessModalOpen] = useState(false)
  const submitFeedbackRef = useRef<HTMLDivElement>(null)
  useScrollToTopOnErrorSignal(submitError)

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const policySummary = formatClaimPolicySummaryLine(
    navState?.policy ?? (policyRef ? { policyRef } : null)
  )

  const windscreenSearch = buildWindscreenSearch(policyRef, typeInQuery)

  const rego =
    policy?.registrationNumber?.trim() ||
    policyRef ||
    '—'
  const vehicleTitle =
    policy?.vehicleName?.trim() ||
    policy?.makeAndModel?.trim() ||
    [policy?.yearBuilt, policy?.make, policy?.model].filter(Boolean).join(' ') ||
    '—'

  const chipLabel =
    draft?.windscreenSmallChip === true ? 'Yes' : draft?.windscreenSmallChip === false ? 'No' : 'NA'

  const replacedLabel =
    draft?.windscreenReplacedAlready === true
      ? 'Yes'
      : draft?.windscreenReplacedAlready === false
        ? 'No'
        : 'NA'

  const basicExcess =
    typeof policy?.basicExcess === 'number' && Number.isFinite(policy.basicExcess)
      ? `$${policy.basicExcess.toLocaleString('en-AU')}`
      : '$500'

  const skipRepairStep = draft?.windscreenReplacedAlready === true

  const backToRepair = () => {
    if (submitting) {
      return
    }
    if (skipRepairStep) {
      navigate(`/claims/windscreen/detail${windscreenSearch}`, {
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    navigate(`/claims/windscreen/repair-option${windscreenSearch}`, {
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }

  const effectivePolicyRef =
    policyRef?.trim() ||
    navState?.policy?.policyRef?.trim() ||
    null

  const handleNext = async () => {
    if (submitting) {
      return
    }
    if (!gstItcComplete) {
      setGstSubmitAttempted(true)
      return
    }
    setGstSubmitAttempted(false)
    gstFieldsRef.current?.flushToDraft()
    const draft = readClaimDraft()
    setSubmitError(null)
    if (!effectivePolicyRef) {
      clientLogWarn('[Claims] submit blocked: missing policyRef', { queryPolicyRef: policyRef, hasNavPolicy: !!navState?.policy })
      setSubmitError('Missing policy reference. Go back and choose your policy again.')
      return
    }
    const desc = draft?.windscreenDescription?.trim() ?? ''
    if (!desc) {
      clientLogWarn('[Claims] submit blocked: empty description')
      setSubmitError('Please add an incident description before submitting.')
      return
    }

    const gstRegistered = draft?.windscreenGstRegistered
    if (typeof gstRegistered !== 'boolean') {
      setSubmitError(
        'Your GST registration answer is missing. Complete the GST and ITC section above before submitting.'
      )
      return
    }
    let itcEntitledPayload: boolean | null = null
    let itcPercentagePayload: number | null = null
    if (gstRegistered === true) {
      const itcEntitled = draft?.windscreenItcEntitled
      if (typeof itcEntitled !== 'boolean') {
        setSubmitError(
          'Your input tax credit (ITC) answer is missing. Complete the GST and ITC section above before submitting.'
        )
        return
      }
      itcEntitledPayload = itcEntitled
      if (itcEntitled === true) {
        const p = draft?.windscreenItcPercentage
        if (typeof p !== 'number' || !Number.isInteger(p) || p < 0 || p > 100) {
          setSubmitError(
            'Your ITC percentage is missing or invalid. Enter a whole number from 0 to 100 in the GST and ITC section above.'
          )
          return
        }
        itcPercentagePayload = p
      }
    }

    const incidentDate = draft?.incidentDateDdMmYyyy?.trim() ?? ''
    if (!incidentDate || !isDdMmYyyyInLocalRange(incidentDate, PORTAL_DATE_MIN, startOfTodayLocal())) {
      clientLogWarn('[Claims] submit blocked: incident date missing or invalid', { incidentDate })
      setSubmitError(
        'When the incident occurred is missing or invalid. Use Back and complete the date and time step.'
      )
      return
    }
    if (isIncidentDdMmYyyyBeforePolicyStart(incidentDate, navState?.policy)) {
      clientLogWarn('[Claims] submit blocked: incident date before policy start', { incidentDate })
      setSubmitError(
        'Incident date cannot be before your policy start date. Use Back and correct the date on the when and where step.'
      )
      return
    }
    const incHm = draft?.incidentTimeHm?.trim() ?? ''
    const incAp = draft?.incidentTimeAmPm
    if (!/^\d{1,2}:\d{2}$/.test(incHm) || (incAp !== 'AM' && incAp !== 'PM')) {
      clientLogWarn('[Claims] submit blocked: incident time incomplete', { incHm, incAp })
      setSubmitError(
        'Incident time is missing or incomplete. Use Back and complete the date and time step.'
      )
      return
    }
    const addrUnk = draft?.incidentAddressUnknown === true
    if (addrUnk) {
      if (!(draft?.incidentState ?? '').trim()) {
        clientLogWarn('[Claims] submit blocked: state missing for unknown address')
        setSubmitError(
          'State is required for the incident location even when you do not know the exact address. Use Back and choose a state on the when and where step.'
        )
        return
      }
    } else {
      if (
        !(draft?.incidentStreet ?? '').trim() ||
        !(draft?.incidentSuburb ?? '').trim() ||
        !isValidAuPostcodeDigits((draft?.incidentPostcode ?? '').trim()) ||
        !(draft?.incidentState ?? '').trim()
      ) {
        clientLogWarn('[Claims] submit blocked: structured address incomplete')
        setSubmitError(
          'Street, suburb, state and postcode are required for the incident location. Use Back and complete that step.'
        )
        return
      }
    }

    const smallChip = draft?.windscreenSmallChip
    if (typeof smallChip !== 'boolean') {
      clientLogWarn('[Claims] submit blocked: windscreenSmallChip not a boolean', { smallChip })
      setSubmitError(
        'Your answer about whether the damage is a small chip is missing. Use Back and complete that step again.'
      )
      return
    }

    const skipRepair = draft?.windscreenReplacedAlready === true

    if (typeof draft?.windscreenReplacedAlready !== 'boolean') {
      clientLogWarn('[Claims] submit blocked: windscreenReplacedAlready not answered', {
        windscreenReplacedAlready: draft?.windscreenReplacedAlready,
      })
      setSubmitError(
        'Your answer about whether the windscreen has already been repaired or replaced is missing. Use Back and complete that step again.'
      )
      return
    }

    if (skipRepair) {
      const company = draft?.windscreenRepairerCompany?.trim() ?? ''
      if (!company) {
        clientLogWarn('[Claims] submit blocked: missing completed repairer on prior-repair path')
        setSubmitError(
          'Who completed the repair is missing. Use Back and add the repairer or business name on the further detail step.'
        )
        return
      }
    }

    const repairOption = draft?.windscreenRepairOption
    if (
      !skipRepair &&
      repairOption !== 'own' &&
      repairOption !== 'trusted'
    ) {
      clientLogWarn('[Claims] submit blocked: invalid windscreenRepairOption', { repairOption })
      setSubmitError(
        'Your repairer choice is missing or invalid. Use Back and select Club 4X4 trusted repairer or Own repairer.'
      )
      return
    }

    const repairOptionSubmit: 'trusted' | 'own' = skipRepair
      ? 'trusted'
      : (repairOption as 'trusted' | 'own')

    setSubmitting(true)
    clientLogInfo('[Claims] UI → POST windscreen FNOL', {
      policyRef: effectivePolicyRef,
      typeInQuery,
      descriptionChars: desc.length,
      repairOption: repairOptionSubmit,
      alreadyReplaced: draft.windscreenReplacedAlready,
      apiBase: import.meta.env.VITE_API_BASE_URL ?? '(default /api)',
    })
    const repairerCompanyNormalized = skipRepair
      ? trimToNull(draft?.windscreenRepairerCompany)
      : repairOptionSubmit === 'trusted'
        ? TRUSTED_WINDSCREEN_REPAIRER_COMPANY
        : trimToNull(draft?.windscreenRepairerCompany)
    const repairerEmailNormalized = trimToNull(draft?.windscreenRepairerEmail)
    const repairerPhoneNormalized = trimToNull(draft?.windscreenRepairerPhone)

    try {
      const res = await submitWindscreenClaim({
        policyRef: effectivePolicyRef,
        productRef: policy?.productRef ?? null,
        policyType: typeInQuery,
        description: desc,
        smallChip,
        repairOption: repairOptionSubmit,
        repairerCompany: repairerCompanyNormalized,
        repairerEmail: repairerEmailNormalized,
        repairerPhone: repairerPhoneNormalized,
        attachmentFileNames: null,
        requiresTowing: draft?.requiresTowing ?? null,
        alreadyReplaced: draft.windscreenReplacedAlready as boolean,
        incidentDateDdMmYyyy: incidentDate,
        incidentTimeHm: incHm,
        incidentTimeAmPm: incAp,
        incidentAddressUnknown: addrUnk,
        incidentAddressSearch: draft?.incidentAddressSearch ?? null,
        incidentStreet: draft?.incidentStreet ?? null,
        incidentSuburb: draft?.incidentSuburb ?? null,
        incidentPostcode: draft?.incidentPostcode ?? null,
        incidentState: draft?.incidentState ?? null,
        gstRegistered,
        itcEntitled: gstRegistered === true ? itcEntitledPayload : null,
        itcPercentage: gstRegistered === true && itcEntitledPayload === true ? itcPercentagePayload : null,
        agentEmailDetailRows: draft
          ? buildFnolSubmitAgentEmailRows(draft, 'windscreen', typeInQuery)
          : undefined,
      })
      if (!res || res.ok !== true) {
        console.error('[Claims] Windscreen FNOL response not successful; keeping draft', res)
        setSubmitError(
          "Your claim wasn't submitted. Please try again, or call 1300 296 296 if the problem continues."
        )
        return
      }
      if (res.emailNotificationFailed) {
        console.error('[Claims] Windscreen FNOL email notification failed; claim was submitted', res)
        setSubmitError(
          "Your claim notification has been lodged, but we couldn't display a claim number yet. Please call 1300 296 296 and we'll confirm it's on file. You do not need to submit the claim again."
        )
        return
      }
      clientLogInfo('[Claims] UI ← API success', {
        claimNumber: res.claimNumber ?? '(none)',
        httpStatus: res.httpStatus,
        entsiaHttpStatus: res.entsiaHttpStatus,
      })
      const windscreenReplacedAlready = draft.windscreenReplacedAlready === true
      clearClaimDraft()
      const submittedPath = `/claims/windscreen/submitted${windscreenSearch}`
      clientLogInfo('[Claims] navigating', { to: submittedPath })
      navigate(submittedPath, {
        state: {
          ...(navState?.policy ? { policy: navState.policy } : {}),
          windscreenClaimSubmitted: true,
          claimNumber: res.claimNumber,
          windscreenReplacedAlready,
          windscreenRepairOption: repairOptionSubmit,
        },
      })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Something went wrong. Please try again or call 1300 296 296.'
      console.error('[Claims] Windscreen FNOL failed', e)
      setSubmitError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const sumInsuredSnippet = formatSumInsuredSnippet(policy)

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={backToRepair}
        backDisabled={submitting}
        title="Confirm your details"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={policySummary}
            claimEntryBranch="windscreen"
            incidentSummary={formatIncidentRailSummary(draft)}
            windscreenGstRegistered={draft?.windscreenGstRegistered ?? null}
            windscreenAbn={draft?.windscreenAbn ?? null}
            windscreenItcEntitled={draft?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={draft?.windscreenItcPercentage ?? null}
            windscreenDescription={draft?.windscreenDescription ?? ''}
            windscreenSmallChip={draft?.windscreenSmallChip ?? null}
            windscreenReplacedAlready={draft?.windscreenReplacedAlready ?? null}
            windscreenRepairOption={draft?.windscreenRepairOption ?? null}
          />
        }
      >
        <FormCard>
          <div className="flex flex-col gap-[12px]">
            <h3 className="m-0 font-body font-semibold text-md leading-md text-text-primary-900">
              GST and input tax credits
            </h3>
            <ClaimGstItcFields
              ref={gstFieldsRef}
              policyRef={policyRef}
              onValidityChange={setGstItcComplete}
              submitAttempted={gstSubmitAttempted}
            />
          </div>
        </FormCard>

        <FormCard>
          <div className="flex flex-col gap-[20px]">
          <div className="flex flex-col gap-[4px]">
            <h2 className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900">
              Check the information below
            </h2>
            <p className="m-0 font-body text-sm leading-sm text-text-tertiary-600">
              Use the back button if you need to change any details.
            </p>
          </div>

          <section className="flex flex-col gap-[12px]" aria-labelledby="review-vehicle-heading">
            <h3 id="review-vehicle-heading" className="m-0 font-body font-semibold text-md leading-md text-text-primary-900">
              Vehicle
            </h3>
            <div className="rounded-xl border border-border-primary bg-background-secondary px-[14px] py-[14px]">
              <div className="content-start flex flex-wrap gap-[8px] items-start">
                <span className="inline-flex items-center border border-text-primary-900 border-solid px-[8px] py-[4px] font-body font-medium text-md leading-md text-text-primary-900">
                  {rego}
                </span>
              </div>
              <p className="mt-[12px] m-0 font-body text-sm font-semibold leading-sm text-text-primary-900">
                {vehicleTitle}
              </p>
            </div>
          </section>

          <section className="flex flex-col gap-[12px]" aria-labelledby="review-incident-heading">
            <h3 id="review-incident-heading" className="m-0 font-body font-semibold text-md leading-md text-text-primary-900">
              What happened
            </h3>
            <div className="rounded-xl border border-border-primary bg-background-primary px-[12px] sm:px-[16px]">
              <ClaimReviewSummaryRow label="Incident type">{CLAIM_FLOW_SUMMARY_WINDSCREEN} damage</ClaimReviewSummaryRow>
              <ClaimReviewSummaryRow label="Date and time">{formatIncidentReviewDateTime(draft)}</ClaimReviewSummaryRow>
              <ClaimReviewSummaryRow label="Location">{formatIncidentReviewLocation(draft)}</ClaimReviewSummaryRow>
            </div>
          </section>
          </div>
        </FormCard>

        <FormCard>
          <div className="flex flex-col gap-[16px]">
          <h3 className="m-0 font-body font-semibold text-md leading-md text-text-primary-900">Further details</h3>
          <div className="rounded-xl border border-border-primary bg-background-primary px-[12px] sm:px-[16px]">
            <ClaimReviewSummaryRow label="Description">
              <ClaimReviewCollapsibleDescription text={draft?.windscreenDescription?.trim() ?? ''} />
            </ClaimReviewSummaryRow>
            <ClaimReviewSummaryRow label="Is the windscreen damage a small chip?">{chipLabel}</ClaimReviewSummaryRow>
            <ClaimReviewSummaryRow label="Has the windscreen already been repaired or replaced?">{replacedLabel}</ClaimReviewSummaryRow>
            <ClaimReviewSummaryRow label="Choice of repairer">
              {repairChoiceLabel(draft?.windscreenRepairOption ?? null, draft)}
            </ClaimReviewSummaryRow>
            <ClaimReviewSummaryRow label="Repairer details">{repairerDetailsSummary(draft)}</ClaimReviewSummaryRow>
          </div>
          </div>
        </FormCard>

        <FormCard>
          <div className="flex flex-col gap-[16px]">
          <h3 className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900">
            Before you submit
          </h3>
          <div>
            <p className="m-0 font-body font-semibold text-md leading-md text-text-primary-900">Excesses</p>
            <ul className="mt-[10px] mb-0 list-disc space-y-[8px] pl-[20px] font-body text-sm leading-sm text-text-secondary-700 marker:text-text-secondary-700">
              <li>
                Your basic excess is <strong className="font-semibold text-text-primary-900">{basicExcess}</strong>
              </li>
              <li>In some cases there may be additional excess — we&apos;ll confirm when we contact you.</li>
            </ul>
            <button
              type="button"
              className="mt-3 self-start border-0 bg-transparent p-0 font-body text-sm font-semibold text-background-brand-solid underline decoration-solid underline-offset-2 hover:text-text-brand-secondary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2"
              onClick={() => setAdditionalExcessModalOpen(true)}
            >
              View additional excesses that may apply
            </button>
          </div>
          </div>
        </FormCard>

        <ClaimAdditionalSupportNotice />

        <div className="sticky bottom-0 z-10 w-full border-t border-border-secondary bg-background-secondary pt-[12px] pb-[8px] md:static md:z-auto md:border-t-0 md:py-0">
          <div ref={submitFeedbackRef} className="mb-[12px] flex flex-col gap-[8px]" aria-live="polite">
            {submitting ? (
              <p className="m-0 font-body text-sm font-semibold text-text-secondary-700">
                Sending your claim… This can take up to a minute.
              </p>
            ) : null}
            {submitError ? (
              <p
                className="m-0 rounded-lg border border-red-200 bg-red-50 px-[14px] py-[12px] font-body text-sm text-red-900"
                role="alert"
              >
                {submitError}
              </p>
            ) : null}
          </div>
          <PrimaryButton
            type="button"
            size="lg"
            fullWidth
            onClick={() => void handleNext()}
            disabled={submitting}
            rightIcon={
              <div className="size-5 text-text-white">
                <ChevronRightIcon />
              </div>
            }
            aria-label={submitting ? 'Submitting' : 'Submit claim'}
          >
            {submitting ? 'Submitting…' : 'Submit claim'}
          </PrimaryButton>
        </div>
      </ClaimsFlowMain>

      <Footer />

      <ClaimAdditionalExcessesModal
        open={additionalExcessModalOpen}
        onClose={() => setAdditionalExcessModalOpen(false)}
        sumInsuredSnippet={sumInsuredSnippet}
      />
    </div>
  )
}
